import { useLingui } from "@lingui/react/macro";
import type { AskTeamSummary } from "@rakazo/contracts";
import { Badge, Checkbox } from "@rakazo/ui-web";

/**
 * A proposed team, one lead per row: initial, name over title, and the department. With
 * `selected`, each row has a tick box the owner uses to keep or leave the lead.
 */
export function TeamLeadList({
  leads,
  label,
  selected,
  onSelectedChange,
}: {
  leads: AskTeamSummary["leads"];
  label?: string;
  selected?: ReadonlySet<string>;
  onSelectedChange?: (name: string, selected: boolean) => void;
}) {
  const { t } = useLingui();
  return (
    <ul className="grid gap-3" aria-label={label ?? t`Your team`} data-testid="team-leads">
      {leads.map((lead) => (
        <li key={lead.name} className="flex min-w-0 items-start gap-3">
          {selected ? (
            <Checkbox
              className="mt-2"
              aria-label={lead.name}
              checked={selected.has(lead.name)}
              onCheckedChange={(checked) => onSelectedChange?.(lead.name, checked)}
            />
          ) : null}
          <span
            aria-hidden
            className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary text-[13px] font-medium text-muted-foreground"
          >
            {lead.name.trim().charAt(0).toUpperCase()}
          </span>
          <span className="min-w-0 flex-1">
            <span dir="auto" className="block break-words text-[14.5px] font-medium leading-5">
              {lead.name}
            </span>
            <span
              dir="auto"
              className="block break-words text-[13px] leading-5 text-muted-foreground"
            >
              {lead.title}
            </span>
          </span>
          <Badge
            variant="secondary"
            className="mt-1 h-auto max-w-[40%] whitespace-normal break-words text-end font-normal"
          >
            {lead.section}
          </Badge>
        </li>
      ))}
    </ul>
  );
}
